import { Button } from "@/components/ui/button";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  onRowClick?: (row: T) => void;
  emptyState?: React.ReactNode;
  pageSize?: number;
}

export function DataTable<T>({
  data,
  columns,
  onRowClick,
  emptyState,
  pageSize,
}: DataTableProps<T>) {
  const paginated = pageSize != null;
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(paginated ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    initialState: paginated ? { pagination: { pageSize, pageIndex: 0 } } : undefined,
  });
  if (data.length === 0 && emptyState) return <>{emptyState}</>;

  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;

  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <table className="w-full border-collapse text-xs">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th
                    key={h.id}
                    className="px-4 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-fg-muted"
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              // biome-ignore lint/a11y/useKeyWithClickEvents: DataTable rows are supplementary UI; keyboard nav is handled at page level
              <tr
                key={row.id}
                onClick={() => onRowClick?.(row.original)}
                className={
                  onRowClick
                    ? "cursor-pointer border-t border-border hover:bg-surface-2"
                    : "border-t border-border"
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-2.5 align-middle">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {paginated && pageCount > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs text-fg-muted">
          <span>
            Page {pageIndex + 1} of {pageCount} · {data.length} rows
          </span>
          <Button
            size="sm"
            variant="default"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            ← Prev
          </Button>
          <Button
            size="sm"
            variant="default"
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          >
            Next →
          </Button>
        </div>
      )}
    </div>
  );
}
